import {
  AutomationEnrollmentExitReasonType as ExitReason,
  AutomationEnrollmentStatusType as Status,
  AutomationExitRuleEventType as ExitEvent,
  AutomationExitRuleKindType as RuleKind,
  AutomationExitRuleScopeType as RuleScope,
  AutomationReentryModeType as ReentryMode,
  AutomationRenewalFailureReasonType as Reason,
  AutomationTriggerParamType as TriggerParam,
  AutomationTriggerType as Trigger,
  AutomationWaitUnitType as WaitUnit,
} from "@/types/graphql/partner.generated";
import type { AutomationGraphRulesDetails } from "@/partner/entities/marketing";
// Только тип: рантайм-импорта automationLabels здесь нет (без цикла).
import type { PlatformTrigger } from "./automationLabels";
import { labelKeyFor } from "./labelKeyFor";

/**
 * Подписи значений цепочек (фаза 4). Здесь только тексты: какие триггеры,
 * события правил выхода, причины отказа и единицы ожидания доступны и с
 * какими пределами, кабинет берёт из правил сервера (journeyRuleLists),
 * своих списков не держит.
 */

/** Общий текст для значения, которого кабинет ещё не знает (рост enum на бэкенде). */
export const JOURNEY_FALLBACK_KEY = "automations.unknown";

/** Ключ текста по значению: общий помощник фичи labelKeyFor, третьей копии не заводим. */
export const labelKey = <T extends string>(
  map: Record<T, string>,
  value: T | null | undefined
): string => labelKeyFor(map, value, JOURNEY_FALLBACK_KEY);

/**
 * Подписи триггеров цепочек: всё, кроме событий платформы и расписания
 * рассылок. Какие из них доступны, говорят правила сервера.
 */
export const JOURNEY_TRIGGER_KEYS: Record<
  Exclude<Trigger, PlatformTrigger | Trigger.ScheduledType>,
  string
> = {
  [Trigger.UserRegisteredType]: "automations.trigger.USER_REGISTERED",
  [Trigger.CheckoutStartedType]: "automations.trigger.CHECKOUT_STARTED",
  [Trigger.PaymentSucceededType]: "automations.trigger.PAYMENT_SUCCEEDED",
  [Trigger.ProductPurchasedType]: "automations.trigger.PRODUCT_PURCHASED",
  [Trigger.ViewStartedType]: "automations.trigger.VIEW_STARTED",
  [Trigger.ViewCompletedType]: "automations.trigger.VIEW_COMPLETED",
  [Trigger.SubscriptionEndingType]: "automations.trigger.SUBSCRIPTION_ENDING",
  [Trigger.SubscriptionCancelledType]:
    "automations.trigger.SUBSCRIPTION_CANCELLED",
  [Trigger.SubscriptionExpiredType]: "automations.trigger.SUBSCRIPTION_EXPIRED",
  [Trigger.RenewalFailedType]: "automations.trigger.RENEWAL_FAILED",
};

/** Подписи полей параметров триггера (AutomationTriggerRule.params). */
export const TRIGGER_PARAM_KEYS: Record<TriggerParam, string> = {
  [TriggerParam.TagIdsType]: "automations.inspector.tags",
  [TriggerParam.ProductIdsType]: "automations.inspector.triggerParams.products",
  [TriggerParam.ContentIdsType]: "automations.inspector.triggerParams.contents",
  [TriggerParam.DaysBeforeType]:
    "automations.inspector.triggerParams.daysBefore",
  [TriggerParam.ReasonsType]: "automations.inspector.triggerParams.reasons",
};

export const EXIT_EVENT_KEYS: Record<ExitEvent, string> = {
  [ExitEvent.PaymentSucceededType]: "automations.exitEvent.PAYMENT_SUCCEEDED",
  [ExitEvent.ProductPurchasedType]: "automations.exitEvent.PRODUCT_PURCHASED",
  [ExitEvent.ViewStartedType]: "automations.exitEvent.VIEW_STARTED",
  [ExitEvent.ViewCompletedType]: "automations.exitEvent.VIEW_COMPLETED",
  [ExitEvent.SubscriptionRenewedType]:
    "automations.exitEvent.SUBSCRIPTION_RENEWED",
  [ExitEvent.SubscriptionRenewalEnabledType]:
    "automations.exitEvent.SUBSCRIPTION_RENEWAL_ENABLED",
  [ExitEvent.SubscriptionExpiredType]:
    "automations.exitEvent.SUBSCRIPTION_EXPIRED",
  [ExitEvent.PurchaseRefundedType]: "automations.exitEvent.PURCHASE_REFUNDED",
  [ExitEvent.MarketingUnsubscribedType]:
    "automations.exitEvent.MARKETING_UNSUBSCRIBED",
};

export const RENEWAL_REASON_KEYS: Record<Reason, string> = {
  [Reason.InsufficientFundsType]:
    "automations.renewalReason.INSUFFICIENT_FUNDS",
  [Reason.CardExpiredType]: "automations.renewalReason.CARD_EXPIRED",
  [Reason.CardDeclinedType]: "automations.renewalReason.CARD_DECLINED",
  [Reason.NoPaymentMethodType]: "automations.renewalReason.NO_PAYMENT_METHOD",
  [Reason.OtherType]: "automations.renewalReason.OTHER",
};

export const WAIT_UNIT_KEYS: Record<WaitUnit, string> = {
  [WaitUnit.HoursType]: "automations.inspector.wait.HOURS",
  [WaitUnit.DaysType]: "automations.inspector.wait.DAYS",
};

/** Порядок фильтра вкладки участников (вид кабинета, не правило). */
export const ENROLLMENT_STATUSES: Status[] = Object.values(Status);

export const ENROLLMENT_STATUS_KEYS: Record<Status, string> = {
  [Status.ActiveType]: "automations.enrollmentStatus.ACTIVE",
  [Status.CompletedType]: "automations.enrollmentStatus.COMPLETED",
  [Status.GoalReachedType]: "automations.enrollmentStatus.GOAL_REACHED",
  [Status.StoppedType]: "automations.enrollmentStatus.STOPPED",
  [Status.ExitedType]: "automations.enrollmentStatus.EXITED",
  [Status.FailedType]: "automations.enrollmentStatus.FAILED",
};

export const EXIT_REASON_KEYS: Record<ExitReason, string> = {
  [ExitReason.GraphChangedType]: "automations.exitReason.GRAPH_CHANGED",
  [ExitReason.UserDeletedType]: "automations.exitReason.USER_DELETED",
  [ExitReason.AutomationArchivedType]:
    "automations.exitReason.AUTOMATION_ARCHIVED",
  [ExitReason.CancelledType]: "automations.exitReason.CANCELLED",
};

export const REENTRY_MODE_KEYS: Record<ReentryMode, string> = {
  [ReentryMode.OnceType]: "automations.reentry.ONCE",
  [ReentryMode.EveryTimeType]: "automations.reentry.EVERY_TIME",
};

export const EXIT_RULE_KIND_KEYS: Record<RuleKind, string> = {
  [RuleKind.GoalType]: "automations.exitRules.kind.GOAL",
  [RuleKind.StopType]: "automations.exitRules.kind.STOP",
};

export const EXIT_RULE_SCOPE_KEYS: Record<RuleScope, string> = {
  [RuleScope.AnyType]: "automations.exitRules.scope.ANY",
  [RuleScope.ContextType]: "automations.exitRules.scope.CONTEXT",
};

/** Списки и пределы цепочки из правил сервера (ревью M9). */
export interface JourneyRuleLists {
  triggers: Trigger[];
  exitEvents: ExitEvent[];
  /** События правил выхода, у которых допустим scope CONTEXT. */
  contextScopeEvents: ExitEvent[];
  renewalReasons: Reason[];
  waitUnits: AutomationGraphRulesDetails["waitUnits"];
  daysBeforeMax: number;
  cooldownDaysMax: number;
  exitRulesMax: number;
  exitRuleNameLength: number;
}

/**
 * Списки триггеров, событий правил выхода, причин отказа, единиц ожидания
 * и пределы прямо из правил вида (useGraphRules, фрагмент
 * AutomationGraphRulesFields); null, пока правил нет.
 */
export function journeyRuleLists(
  rules: AutomationGraphRulesDetails | null
): JourneyRuleLists | null {
  if (!rules) return null;
  return {
    triggers: rules.triggers,
    exitEvents: rules.exitRuleEvents,
    contextScopeEvents: rules.contextScopeEvents,
    renewalReasons: rules.renewalReasons,
    waitUnits: rules.waitUnits,
    daysBeforeMax: rules.limits.daysBeforeMax,
    cooldownDaysMax: rules.limits.cooldownDaysMax,
    exitRulesMax: rules.limits.exitRules,
    exitRuleNameLength: rules.limits.exitRuleNameLength,
  };
}
